import { useMutation, useQueryClient } from "@tanstack/react-query";
import { titleQueryKeys } from "@/lib/react-query/keyFactory";
import { participantAdd, participantRemove } from "@/proofingService-client/sdk.gen";
import type { ParticipantAddResponse } from "@/proofingService-client/types.gen";

interface ParticipantsCache {
  data: ParticipantAddResponse[];
  [key: string]: unknown;
}

export interface UseAddParticipantOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for adding a participant to a proofing title.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useAddParticipant = (options?: UseAddParticipantOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ titleId, userId }: { titleId: string; userId: string }) => {
      const response = await participantAdd({
        path: { titleId },
        body: { userId },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Optimistically update participants cache
      queryClient.setQueryData(
        titleQueryKeys.participants(variables.titleId),
        (old: ParticipantsCache | undefined) => {
          if (!old?.data) return old;

          // Check if participant already exists (might be soft-deleted)
          const existingIndex = old.data.findIndex((p) => p.userId === data.userId);

          let updatedData: ParticipantAddResponse[];
          if (existingIndex !== -1) {
            // Update existing participant (undelete it)
            updatedData = [...old.data];
            updatedData[existingIndex] = { ...data, deleted: false };
          } else {
            // Add new participant
            updatedData = [...old.data, data];
          }

          return {
            ...old,
            data: updatedData,
          };
        },
      );

      // Also invalidate title details to refresh round participants
      queryClient.invalidateQueries({
        queryKey: titleQueryKeys.detail(variables.titleId),
      });

      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to add participant to proofing title:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseRemoveParticipantOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for removing a participant from a proofing title.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useRemoveParticipant = (options?: UseRemoveParticipantOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ titleId, userId }: { titleId: string; userId: string }) => {
      const response = await participantRemove({
        path: { titleId, userId },
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Optimistically update participants cache (soft delete)
      queryClient.setQueryData(
        titleQueryKeys.participants(variables.titleId),
        (old: ParticipantsCache | undefined) => {
          if (!old?.data) return old;

          // Mark the participant as deleted (soft delete)
          return {
            ...old,
            data: old.data.map((p) =>
              p.userId === variables.userId ? { ...p, deleted: true } : p,
            ),
          };
        },
      );

      // Also invalidate title details to refresh round participants
      queryClient.invalidateQueries({
        queryKey: titleQueryKeys.detail(variables.titleId),
      });

      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error("Failed to remove participant from proofing title:", error);
      options?.onError?.(error);
    },
  });
};
